import React from "react";
import { listSites } from "../api/sites";
import {
  previewCopyScada,
  startCopyJob,
  fetchCopyJobStatus,
  cancelCopyJob,
  checkAnalyticsExisting,
  startAnalyticsJob,
  fetchAnalyticsStatus,
  cancelAnalyticsJob,
  checkAvailabilityReportSnapshots,
  startAvailabilityReportJob,
  fetchAvailabilityReportStatus,
  cancelAvailabilityReportJob,
} from "../api/dataMaintenance";
import { Card, CardContent, CardHeader } from "../ui/card";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const MAX_RANGE_DAYS = 30;

const daysBetween = (start, end) => {
  if (!start || !end) return 0;
  const startDate = new Date(start);
  const endDate = new Date(end);
  return Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
};

const formatNumber = (value) => (value === null || value === undefined ? "—" : value.toLocaleString());

export default function DataMaintenance() {
  const [sites, setSites] = React.useState([]);
  const [siteId, setSiteId] = React.useState("");
  const [analyticsSiteId, setAnalyticsSiteId] = React.useState("");
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [preview, setPreview] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [jobId, setJobId] = React.useState(null);
  const [jobStatus, setJobStatus] = React.useState(null);
  const [analyticsMode, setAnalyticsMode] = React.useState("day");
  const [analyticsStart, setAnalyticsStart] = React.useState("");
  const [analyticsEnd, setAnalyticsEnd] = React.useState("");
  const [analyticsJobId, setAnalyticsJobId] = React.useState(null);
  const [analyticsStatus, setAnalyticsStatus] = React.useState(null);
  const [analyticsError, setAnalyticsError] = React.useState("");
  const [showAnalyticsConfirm, setShowAnalyticsConfirm] = React.useState(false);
  const [reportSiteId, setReportSiteId] = React.useState("");
  const [reportMode, setReportMode] = React.useState("day");
  const [reportStart, setReportStart] = React.useState("");
  const [reportEnd, setReportEnd] = React.useState("");
  const [reportJobId, setReportJobId] = React.useState(null);
  const [reportStatus, setReportStatus] = React.useState(null);
  const [reportError, setReportError] = React.useState("");
  const [showReportConfirm, setShowReportConfirm] = React.useState(false);

  React.useEffect(() => {
    const loadSites = async () => {
      try {
        const data = await listSites();
        setSites(data || []);
        if (data && data.length) {
          setSiteId(String(data[0].id));
          setAnalyticsSiteId(String(data[0].id));
          setReportSiteId(String(data[0].id));
        }
      } catch {
        setError("Failed to load sites.");
      }
    };
    loadSites();
  }, []);

  React.useEffect(() => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    setEndDate(today.toISOString().slice(0, 10));
    setStartDate(yesterday.toISOString().slice(0, 10));
    setAnalyticsEnd(today.toISOString().slice(0, 10));
    setAnalyticsStart(yesterday.toISOString().slice(0, 10));
    setReportEnd(today.toISOString().slice(0, 10));
    setReportStart(yesterday.toISOString().slice(0, 10));
  }, []);

  React.useEffect(() => {
    // Keep analytics site in sync when user picks a new site in copy block
    if (siteId && !analyticsSiteId) {
      setAnalyticsSiteId(siteId);
    }
    if (siteId && !reportSiteId) {
      setReportSiteId(siteId);
    }
  }, [siteId, analyticsSiteId, reportSiteId]);

  React.useEffect(() => {
    if (!jobId) return undefined;
    let cancelled = false;

    const fetchStatus = async () => {
      try {
        const data = await fetchCopyJobStatus(jobId);
        if (cancelled) return;
        setJobStatus(data);
        if (data.status !== "running" && data.status !== "pending") {
          setResult(data);
          setJobId(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError("Failed to fetch copy progress.");
        }
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [jobId]);

  React.useEffect(() => {
    if (!analyticsJobId) return undefined;
    let cancelled = false;

    const fetchStatus = async () => {
      try {
        const data = await fetchAnalyticsStatus(analyticsJobId);
        if (cancelled) return;
        setAnalyticsStatus(data);
        if (!["running", "pending"].includes(data.status)) {
          setAnalyticsJobId(null);
        }
      } catch (err) {
        if (!cancelled) {
          setAnalyticsError("Failed to fetch analytics progress.");
        }
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [analyticsJobId]);

  React.useEffect(() => {
    if (!reportJobId) return undefined;
    let cancelled = false;

    const fetchStatus = async () => {
      try {
        const data = await fetchAvailabilityReportStatus(reportJobId);
        if (cancelled) return;
        setReportStatus(data);
        if (!["running", "pending"].includes(data.status)) {
          setReportJobId(null);
        }
      } catch (err) {
        if (!cancelled) {
          setReportError("Failed to fetch report regeneration progress.");
        }
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [reportJobId]);

  const validateRange = () => {
    if (!siteId) return "Select a site.";
    if (!startDate || !endDate) return "Select a start and end date.";
    if (new Date(startDate) > new Date(endDate)) return "Start date must be before end date.";
    if (daysBetween(startDate, endDate) > MAX_RANGE_DAYS) {
      return `Date range cannot exceed ${MAX_RANGE_DAYS} days.`;
    }
    return "";
  };

  const handlePreview = async () => {
    setError("");
    setResult(null);
    setPreview(null);
    setShowConfirm(false);
    setJobStatus(null);
    const validationError = validateRange();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const data = await previewCopyScada({ siteId, startDate, endDate });
      setPreview(data);
      if (data.conflict_rows > 0) {
        setShowConfirm(true);
      } else if (data.prepared_rows > 0) {
        await startJob("skip");
      }
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.detail || "Failed to preview copy.");
    } finally {
      setLoading(false);
    }
  };

  const startJob = async (mode) => {
    setError("");
    setLoading(true);
    try {
      const data = await startCopyJob({
        siteId,
        startDate,
        endDate,
        onConflict: mode,
      });
      setJobId(data.job_id);
      setJobStatus(data);
      setShowConfirm(false);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.detail || "Failed to start copy job.");
    } finally {
      setLoading(false);
    }
  };

  const rangeDays = daysBetween(startDate, endDate);
  const progressTotal = jobStatus?.prepared_rows || preview?.prepared_rows || 0;
  const progressCurrent = jobStatus?.processed_rows || 0;
  const progressPct = progressTotal ? Math.min(100, Math.round((progressCurrent / progressTotal) * 100)) : 0;
  const jobActive = jobStatus && ["pending", "running"].includes(jobStatus.status);

  const analyticsDaysBetween = () => {
    if (analyticsMode !== "custom") return null;
    if (!analyticsStart || !analyticsEnd) return null;
    return daysBetween(analyticsStart, analyticsEnd);
  };

  const validateAnalyticsRange = () => {
    if (!analyticsSiteId) return "Select a site.";
    if (analyticsMode === "custom") {
      if (!analyticsStart || !analyticsEnd) return "Select a start and end date.";
      if (new Date(analyticsStart) > new Date(analyticsEnd)) return "Start date must be before end date.";
      const span = analyticsDaysBetween();
      if (span && span > MAX_RANGE_DAYS) return `Date range cannot exceed ${MAX_RANGE_DAYS} days.`;
    }
    return "";
  };

  const handleCancelAnalytics = async () => {
    if (!analyticsJobId) return;
    try {
      const data = await cancelAnalyticsJob(analyticsJobId);
      setAnalyticsStatus(data);
      setAnalyticsJobId(null);
    } catch (err) {
      console.error(err);
      setAnalyticsError("Failed to cancel analytics job.");
    }
  };

  const computeAnalyticsDates = () => {
    if (analyticsMode === "day") {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 1);
      return {
        startDate: start.toISOString().slice(0, 10),
        endDate: end.toISOString().slice(0, 10),
      };
    }
    if (analyticsMode === "week") {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 7);
      return {
        startDate: start.toISOString().slice(0, 10),
        endDate: end.toISOString().slice(0, 10),
      };
    }
    if (analyticsMode === "month") {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 30);
      return {
        startDate: start.toISOString().slice(0, 10),
        endDate: end.toISOString().slice(0, 10),
      };
    }
    return { startDate: analyticsStart, endDate: analyticsEnd };
  };

  const handleAnalyticsRun = async () => {
    setAnalyticsError("");
    const validation = validateAnalyticsRange();
    if (validation) {
      setAnalyticsError(validation);
      return;
    }
    const { startDate, endDate } = computeAnalyticsDates();
    try {
      const check = await checkAnalyticsExisting({ siteId: analyticsSiteId, startDate, endDate });
      if (check.has_data) {
        setShowAnalyticsConfirm(true);
      } else {
        await startAnalytics("skip");
      }
    } catch (err) {
      console.error(err);
      setAnalyticsError("Failed to check existing analytics data.");
    }
  };

  const startAnalytics = async (conflictMode) => {
    setAnalyticsError("");
    try {
      const { startDate, endDate } = computeAnalyticsDates();
      const data = await startAnalyticsJob({
        siteId: analyticsSiteId,
        mode: analyticsMode,
        startDate,
        endDate,
        onConflict: conflictMode === "overwrite" ? "overwrite" : null,
      });
      setAnalyticsJobId(data.job_id);
      setAnalyticsStatus(data);
      setShowAnalyticsConfirm(false);
    } catch (err) {
      console.error(err);
      setAnalyticsError(err?.response?.data?.detail || "Failed to start analytics job.");
    }
  };

  const analyticsTotalDays = analyticsStatus?.total_days || 0;
  const analyticsProcessed = analyticsStatus?.processed_days || 0;
  const analyticsPct = analyticsTotalDays ? Math.min(100, Math.round((analyticsProcessed / analyticsTotalDays) * 100)) : 0;
  const analyticsJobActive = analyticsStatus && ["pending", "running"].includes(analyticsStatus.status);

  const reportDaysBetween = () => {
    if (reportMode !== "custom") return null;
    if (!reportStart || !reportEnd) return null;
    return daysBetween(reportStart, reportEnd);
  };

  const computeReportDates = () => {
    if (reportMode === "day") {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 1);
      return { startDate: start.toISOString().slice(0, 10), endDate: end.toISOString().slice(0, 10) };
    }
    if (reportMode === "week") {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 7);
      return { startDate: start.toISOString().slice(0, 10), endDate: end.toISOString().slice(0, 10) };
    }
    if (reportMode === "month") {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 30);
      return { startDate: start.toISOString().slice(0, 10), endDate: end.toISOString().slice(0, 10) };
    }
    return { startDate: reportStart, endDate: reportEnd };
  };

  const validateReportRange = () => {
    if (!reportSiteId) return "Select a site.";
    if (reportMode === "custom") {
      if (!reportStart || !reportEnd) return "Select a start and end date.";
      if (new Date(reportStart) > new Date(reportEnd)) return "Start date must be before end date.";
      const span = reportDaysBetween();
      if (span && span > MAX_RANGE_DAYS) return `Date range cannot exceed ${MAX_RANGE_DAYS} days.`;
    }
    return "";
  };

  const startReportRegeneration = async (conflictMode) => {
    setReportError("");
    try {
      const { startDate, endDate } = computeReportDates();
      const data = await startAvailabilityReportJob({
        siteId: reportSiteId,
        mode: reportMode,
        startDate,
        endDate,
        onConflict: conflictMode === "overwrite" ? "overwrite" : null,
      });
      setReportJobId(data.job_id);
      setReportStatus(data);
      setShowReportConfirm(false);
    } catch (err) {
      console.error(err);
      setReportError(err?.response?.data?.detail || "Failed to start report regeneration.");
    }
  };

  const handleReportRegeneration = async () => {
    setReportError("");
    const validation = validateReportRange();
    if (validation) {
      setReportError(validation);
      return;
    }
    const { startDate, endDate } = computeReportDates();
    try {
      const check = await checkAvailabilityReportSnapshots({ siteId: reportSiteId, startDate, endDate });
      if (check.has_data) {
        setShowReportConfirm(true);
      } else {
        await startReportRegeneration("skip");
      }
    } catch (err) {
      console.error(err);
      setReportError(err?.response?.data?.detail || "Failed to check existing report snapshots.");
    }
  };

  const handleCancelReportJob = async () => {
    if (!reportJobId) return;
    try {
      const data = await cancelAvailabilityReportJob(reportJobId);
      setReportStatus(data);
      setReportJobId(null);
    } catch (err) {
      console.error(err);
      setReportError("Failed to cancel report regeneration job.");
    }
  };

  const reportTotalDays = reportStatus?.total_days || 0;
  const reportProcessed = reportStatus?.processed_days || 0;
  const reportPct = reportTotalDays ? Math.min(100, Math.round((reportProcessed / reportTotalDays) * 100)) : 0;
  const reportJobActive = reportStatus && ["pending", "running"].includes(reportStatus.status);

  const handleCancelJob = async () => {
    if (!jobId) return;
    try {
      const data = await cancelCopyJob(jobId);
      setJobStatus(data);
      setResult(data);
      setJobId(null);
    } catch (err) {
      console.error(err);
      setError("Failed to cancel job.");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Data Maintenance</h1>
        <p className="text-slate-600">
          Copy SCADA import rows into normalized data for analytics and reprocess analytics.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="font-semibold text-slate-800">Copy Imported DATA</div>
          <p className="text-sm text-slate-500">
            Copy SCADA import rows into normalized data for analytics. A maximum of {MAX_RANGE_DAYS} days can be processed
          at once.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-slate-700">From</label>
                  <input
                    type="date"
                    className="rounded-md border px-3 py-2 text-sm"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-slate-700">To</label>
                  <input
                    type="date"
                    className="rounded-md border px-3 py-2 text-sm"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="text-xs text-slate-500">
                {rangeDays > 0 ? `${rangeDays} day(s) selected.` : "Select up to 30 days."}
              </div>

              {error && <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

              <Button disabled={loading || Boolean(jobId)} onClick={handlePreview}>
                {loading ? "Working..." : "Copy data"}
              </Button>
              {jobStatus && (
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  <div className="flex justify-between">
                    <span>Status</span>
                    <span className="font-semibold">{jobStatus.status}</span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-slate-200">
                    <div
                      className="h-2 rounded-full bg-blue-600"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                  <div className="mt-1 flex justify-between text-xs text-slate-600">
                    <span>
                      {formatNumber(progressCurrent)} / {formatNumber(progressTotal || 0)} processed
                    </span>
                    <span>{progressPct}%</span>
                  </div>
                  {jobActive && (
                    <div className="mt-3">
                      <Button
                        onClick={handleCancelJob}
                        className="w-full bg-gray-100 text-red-900 hover:bg-gray-300 border border-slate-300"
                      >
                        Cancel job
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-600">Site</span>
                <select
                  className="rounded-md border px-2 py-1 text-sm font-semibold text-slate-900 bg-white"
                  value={siteId}
                  onChange={(e) => setSiteId(e.target.value)}
                >
                  {sites.map((site) => (
                    <option key={`copy-site-${site.id}`} value={site.id}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Window</span>
                <span className="font-semibold text-slate-900">
                  {startDate || "—"} → {endDate || "—"}
                </span>
              </div>

              {preview && (
                <div className="rounded-md border border-amber-100 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <div className="font-semibold">Preview</div>
                  <div className="flex justify-between">
                    <span>Source rows</span>
                    <span className="font-semibold">{formatNumber(preview.source_rows)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Prepared rows</span>
                    <span className="font-semibold">{formatNumber(preview.prepared_rows)}</span>
                  </div>
                  {"ready_to_insert" in preview && (
                    <div className="flex justify-between">
                      <span>Ready to insert</span>
                      <span className="font-semibold">{formatNumber(preview.ready_to_insert)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Conflicts detected</span>
                    <span className="font-semibold">{formatNumber(preview.conflict_rows)}</span>
                  </div>
                </div>
              )}

              {result && (
                <div className="rounded-md border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                  <div className="font-semibold">Copy result</div>
                  <div className="flex justify-between">
                    <span>Inserted</span>
                    <span className="font-semibold">{formatNumber(result.inserted_rows)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Skipped</span>
                    <span className="font-semibold">{formatNumber(result.skipped_rows)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Overwritten</span>
                    <span className="font-semibold">{formatNumber(result.overwritten_rows)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Status</span>
                    <span className="font-semibold">{result.status}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Analytics Section */}
      <Card>
        <CardHeader>
          <div className="font-semibold text-slate-800">Run Analytics</div>
          <p className="text-sm text-slate-500">
            Reprocess derived metrics for a recent period or a custom window. A maximum of {MAX_RANGE_DAYS} days can be reprocessed at once. This runs inverter analytics
            and availability; tickets are not affected.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input
                    type="radio"
                    name="analytics-range"
                    checked={analyticsMode === "day"}
                    onChange={() => setAnalyticsMode("day")}
                  />
                  Last 24 hours
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input
                    type="radio"
                    name="analytics-range"
                    checked={analyticsMode === "week"}
                    onChange={() => setAnalyticsMode("week")}
                  />
                  Last 1 week
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input
                    type="radio"
                    name="analytics-range"
                    checked={analyticsMode === "month"}
                    onChange={() => setAnalyticsMode("month")}
                  />
                  Last 1 month
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input
                    type="radio"
                    name="analytics-range"
                    checked={analyticsMode === "custom"}
                    onChange={() => setAnalyticsMode("custom")}
                  />
                  Custom period
                </label>
              </div>

              {analyticsMode === "custom" && (
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-slate-700">From</label>
                    <input
                      type="date"
                      className="rounded-md border px-3 py-2 text-sm"
                      value={analyticsStart}
                      onChange={(e) => setAnalyticsStart(e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-slate-700">To</label>
                    <input
                      type="date"
                      className="rounded-md border px-3 py-2 text-sm"
                      value={analyticsEnd}
                      onChange={(e) => setAnalyticsEnd(e.target.value)}
                    />
                  </div>
                  <div className="md:col-span-2 text-xs text-slate-500">
                    {analyticsDaysBetween()
                      ? `${analyticsDaysBetween()} day(s) selected.`
                      : "Select up to 30 days."}
                  </div>
                </div>
              )}

              {analyticsError && (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {analyticsError}
                </div>
              )}

              <Button onClick={handleAnalyticsRun} disabled={analyticsJobActive}>
                Run analytics
              </Button>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-600">Site</span>
                <select
                  className="rounded-md border px-2 py-1 text-sm font-semibold text-slate-900 bg-white"
                  value={analyticsSiteId || siteId}
                  onChange={(e) => setAnalyticsSiteId(e.target.value)}
                >
                  {sites.map((site) => (
                    <option key={`analytics-site-${site.id}`} value={site.id}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Window</span>
                <span className="font-semibold text-slate-900">
                  {computeAnalyticsDates().startDate || "—"} → {computeAnalyticsDates().endDate || "—"}
                </span>
              </div>

              {analyticsStatus && (
                <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                  <div className="flex justify-between">
                    <span>Status</span>
                    <span className="font-semibold">{analyticsStatus.status}</span>
                  </div>
                  {analyticsStatus.message && (
                    <div className="mt-1 text-xs text-rose-600">{analyticsStatus.message}</div>
                  )}
                  <div className="mt-2 h-2 rounded-full bg-slate-200">
                    <div
                      className="h-2 rounded-full bg-blue-600"
                      style={{ width: `${analyticsPct}%` }}
                    />
                  </div>
                  <div className="mt-1 flex justify-between text-xs text-slate-600">
                    <span>
                      {formatNumber(analyticsProcessed)} / {formatNumber(analyticsTotalDays || 0)} day(s)
                    </span>
                    <span>{analyticsPct}%</span>
                  </div>
                  {analyticsJobActive && (
                    <div className="mt-3">
                      <Button
                        onClick={handleCancelAnalytics}
                        className="w-full bg-slate-200 text-slate-900 hover:bg-slate-300 border border-slate-300"
                      >
                        Cancel job
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="font-semibold text-slate-800">Regenerate Inverter Availability Reports</div>
          <p className="text-sm text-slate-500">
            Regenerates stored inverter availability daily report snapshots from existing availability minute data. To
            recalculate minute availability from corrected telemetry, run Analytics first.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input type="radio" name="report-range" checked={reportMode === "day"} onChange={() => setReportMode("day")} />
                  Last 24 hours
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input type="radio" name="report-range" checked={reportMode === "week"} onChange={() => setReportMode("week")} />
                  Last 1 week
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input type="radio" name="report-range" checked={reportMode === "month"} onChange={() => setReportMode("month")} />
                  Last 1 month
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input type="radio" name="report-range" checked={reportMode === "custom"} onChange={() => setReportMode("custom")} />
                  Custom period
                </label>
              </div>

              {reportMode === "custom" && (
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-slate-700">From</label>
                    <input
                      type="date"
                      className="rounded-md border px-3 py-2 text-sm"
                      value={reportStart}
                      onChange={(e) => setReportStart(e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-slate-700">To</label>
                    <input
                      type="date"
                      className="rounded-md border px-3 py-2 text-sm"
                      value={reportEnd}
                      onChange={(e) => setReportEnd(e.target.value)}
                    />
                  </div>
                  <div className="md:col-span-2 text-xs text-slate-500">
                    {reportDaysBetween() ? `${reportDaysBetween()} day(s) selected.` : "Select up to 30 days."}
                  </div>
                </div>
              )}

              {reportError && (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {reportError}
                </div>
              )}

              <Button onClick={handleReportRegeneration} disabled={reportJobActive}>
                Regenerate reports
              </Button>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-600">Site</span>
                <select
                  className="rounded-md border px-2 py-1 text-sm font-semibold text-slate-900 bg-white"
                  value={reportSiteId || siteId}
                  onChange={(e) => setReportSiteId(e.target.value)}
                >
                  {sites.map((site) => (
                    <option key={`availability-report-site-${site.id}`} value={site.id}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Window</span>
                <span className="font-semibold text-slate-900">
                  {computeReportDates().startDate || "—"} → {computeReportDates().endDate || "—"}
                </span>
              </div>

              {reportStatus && (
                <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                  <div className="flex justify-between">
                    <span>Status</span>
                    <span className="font-semibold">{reportStatus.status}</span>
                  </div>
                  {reportStatus.message && <div className="mt-1 text-xs text-rose-600">{reportStatus.message}</div>}
                  <div className="mt-2 h-2 rounded-full bg-slate-200">
                    <div className="h-2 rounded-full bg-blue-600" style={{ width: `${reportPct}%` }} />
                  </div>
                  <div className="mt-1 flex justify-between text-xs text-slate-600">
                    <span>
                      {formatNumber(reportProcessed)} / {formatNumber(reportTotalDays || 0)} day(s)
                    </span>
                    <span>{reportPct}%</span>
                  </div>
                  {reportJobActive && (
                    <div className="mt-3">
                      <Button
                        onClick={handleCancelReportJob}
                        className="w-full bg-slate-200 text-slate-900 hover:bg-slate-300 border border-slate-300"
                      >
                        Cancel job
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Existing data found</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            <p>
              {formatNumber(preview?.conflict_rows)} existing normalized rows match this window. Choose how to proceed:
            </p>
            <div className="flex flex-col gap-2">
              <Button
                disabled={loading}
                onClick={() => startJob("skip")}
                className="justify-start bg-blue-600 text-white hover:bg-blue-700 border border-blue-600"
              >
                Skip existing and insert new rows
              </Button>
              <Button
                disabled={loading}
                onClick={() => startJob("overwrite")}
                className="justify-start bg-red-600 text-white hover:bg-red-700 border border-red-600"
              >
                Overwrite existing rows with import values
              </Button>
              <Button
                onClick={() => setShowConfirm(false)}
                className="justify-start bg-slate-200 text-slate-900 hover:bg-slate-300 border border-slate-300"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showAnalyticsConfirm} onOpenChange={setShowAnalyticsConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Existing analytics data</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            <p>Derived analytics already exist for this window. Proceeding will overwrite them.</p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => startAnalytics("overwrite")}
                className="justify-start bg-blue-600 text-white hover:bg-blue-700 border border-blue-600"
              >
                Proceed (overwrite)
              </Button>
              <Button
                onClick={() => setShowAnalyticsConfirm(false)}
                className="justify-start bg-slate-200 text-slate-900 hover:bg-slate-300 border border-slate-300"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showReportConfirm} onOpenChange={setShowReportConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Existing availability report snapshots</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            <p>Stored inverter availability report snapshots already exist for this window. Proceeding will overwrite them.</p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => startReportRegeneration("overwrite")}
                className="justify-start bg-blue-600 text-white hover:bg-blue-700 border border-blue-600"
              >
                Proceed (overwrite)
              </Button>
              <Button
                onClick={() => setShowReportConfirm(false)}
                className="justify-start bg-slate-200 text-slate-900 hover:bg-slate-300 border border-slate-300"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
